<script setup>
/**
 * @description 联系我们弹窗内容（桌面 hover / 移动端抽屉共用）
 * 总部信息写死；国内销售电话/邮箱先兜底，再覆盖 agent-contact。
 */
import { computed, onMounted, ref } from 'vue'
import { salesContactApi } from '~/api/common/salesContact'
import { resolveAgentCertificationContact } from '~/utils/store/agentCertificationContact'

const { withBase } = useBasePath()
const FOOTER_SALES_ORGANIZATION = 'TDC'
const fallbackSalesContact = resolveAgentCertificationContact(FOOTER_SALES_ORGANIZATION)
const salesPhone = ref(fallbackSalesContact.phone)
const salesEmail = ref(fallbackSalesContact.email)

const CONTACT_US_ICON = {
  company: '/images/footer/link_us/vector.svg',
  tel: '/images/footer/link_us/tel.svg',
  email: '/images/footer/link_us/email.svg',
  addr: '/images/footer/link_us/addr.svg',
}

onMounted(() => {
  if (!import.meta.client) return
  salesContactApi
    .getAgentCertificationContact({ salesOrganization: FOOTER_SALES_ORGANIZATION })
    .then((contact) => {
      const next = resolveAgentCertificationContact(FOOTER_SALES_ORGANIZATION, contact)
      salesPhone.value = next.phone
      salesEmail.value = next.email
    })
    .catch((error) => {
      console.warn('[contact-us] failed to load sales contact, using fallback', error)
    })
})

/**
 * @typedef {Object} ContactUsPopupItem
 * @property {string} key
 * @property {string} icon
 * @property {string} text
 * @property {string} [href]
 * @property {boolean} [plainLink] - 使用普通文本色（非品牌色链接）
 */

const CONTACT_US_SECTIONS = computed(() => [
  {
    title: '腾达总部',
    items: [
      {
        key: 'company',
        icon: CONTACT_US_ICON.company,
        text: '深圳市吉祥腾达科技有限公司',
      },
      {
        key: 'hq-tel',
        icon: CONTACT_US_ICON.tel,
        text: '+86-755-2765 7098',
      },
      {
        key: 'hq-email',
        icon: CONTACT_US_ICON.email,
        text: 'brand@tenda.cn',
        href: 'mailto:brand@tenda.cn',
      },
      {
        key: 'hq-addr',
        icon: CONTACT_US_ICON.addr,
        text: '深圳市南山区中山园路1001号国际E城腾达科技大厦（518052）',
      },
    ],
  },
  {
    title: '国内销售合作',
    items: [
      {
        key: 'sales-tel',
        icon: CONTACT_US_ICON.tel,
        text: salesPhone.value,
      },
      {
        key: 'sales-email',
        icon: CONTACT_US_ICON.email,
        text: salesEmail.value,
        href: salesEmail.value ? `mailto:${salesEmail.value}` : undefined,
        plainLink: true,
      },
    ],
  },
])
</script>

<template>
  <div class="contact-us-popup-content">
    <template v-for="(section, sectionIndex) in CONTACT_US_SECTIONS" :key="section.title">
      <div v-if="sectionIndex > 0" class="contact-us-popup-content__divider" />

      <div class="contact-us-popup-content__section">
        <h4 class="contact-us-popup-content__section-title">{{ section.title }}</h4>
        <div v-for="item in section.items" :key="item.key" class="contact-us-popup-content__item">
          <div class="contact-us-popup-content__icon-wrap">
            <img :src="withBase(item.icon)" alt="" class="contact-us-popup-content__icon" />
          </div>
          <a
            v-if="item.href"
            :href="item.href"
            class="contact-us-popup-content__link"
            :class="{ 'contact-us-popup-content__link--plain': item.plainLink }"
          >
            {{ item.text }}
          </a>
          <span v-else class="contact-us-popup-content__text">{{ item.text }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.contact-us-popup-content {
  &__section {
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;
  }

  &__section-title {
    margin: 0 0 $spacing-xs;
    color: $text-title-color;
    font-size: $font-size-body-1;
    font-weight: $font-weight-medium;
    line-height: $line-height-body-1;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: $spacing-xs;
  }

  &__icon-wrap {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-top: calc((#{$line-height-body-1} - 20px) / 2);
  }

  &__icon {
    display: block;
    width: 20px;
    height: 20px;
    object-fit: contain;
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
    color: $text-title-color;
    font-size: $font-size-body-1;
    line-height: $line-height-body-1;
    word-break: break-word;
  }

  &__link {
    flex: 1;
    color: $primary-color;
    font-size: $font-size-body-1;
    line-height: $line-height-body-1;
    text-decoration: underline;
    text-underline-position: from-font;

    &:hover {
      color: $primary-color-hover;
    }

    &--plain {
      color: $text-title-color;
      text-decoration: underline;
      text-underline-position: from-font;

      &:hover {
        color: $text-title-color;
      }
    }
  }

  &__divider {
    width: 100%;
    height: 1px;
    margin: $spacing-xl 0;
    background: $border-light-color;
  }
}
</style>
